import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Plus, Edit2, Trash2, UserCircle, Search } from 'lucide-react'
import toast from 'react-hot-toast'
import { usersApi, type User, type CreateUserData, type UpdateUserData } from '@/services/usersApi'
import { teamsApi, type Team } from '@/services/teamsApi'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import Modal from '@/components/ui/modal'
import { MultiSelect, type MultiSelectOption } from '@/components/ui/multi-select'
import { useAuthStore } from '@/store/authStore'

const userSchema = z.object({
  email: z.string().email('Email inválido'),
  name: z.string().min(1, 'El nombre es requerido'),
  role: z.enum(['owner', 'admin', 'agent', 'viewer'], {
    errorMap: () => ({ message: 'Rol inválido' }),
  }),
  teamIds: z.array(z.string()).optional(),
  password: z.string().optional(),
})

type UserFormData = z.infer<typeof userSchema>

export default function UsersManager() {
  const tenantId = useAuthStore((s) => s.tenantId)
  const [users, setUsers] = useState<User[]>([])
  const [teams, setTeams] = useState<Team[]>([])
  const [loading, setLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [removingUser, setRemovingUser] = useState<string | null>(null)
  const [search, setSearch] = useState('')

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch,
  } = useForm<UserFormData>({
    resolver: zodResolver(userSchema as any),
    defaultValues: {
      email: '',
      name: '',
      role: 'agent',
      teamIds: [],
      password: '',
    },
  })

  const selectedTeamIds = watch('teamIds') || []
  const isEditing = !!editingUser

  useEffect(() => {
    loadData()
  }, [tenantId])

  const loadData = async () => {
    try {
      setLoading(true)
      const [usersData, teamsData] = await Promise.all([
        usersApi.getUsers(tenantId || undefined),
        teamsApi.getTeams(tenantId || undefined),
      ])
      setUsers(usersData)
      setTeams(teamsData)
    } catch (error) {
      console.error('Error loading data:', error)
      toast.error('Error al cargar los datos')
    } finally {
      setLoading(false)
    }
  }

  const openCreateModal = () => {
    setEditingUser(null)
    reset({
      email: '',
      name: '',
      role: 'agent',
      teamIds: [],
      password: '',
    })
    setIsModalOpen(true)
  }

  const openEditModal = (user: User) => {
    setEditingUser(user)
    reset({
      email: user.email,
      name: user.name || '',
      role: user.role,
      teamIds: user.teams.map((t) => t.id),
      password: '', // Don't pre-fill password
    })
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setIsModalOpen(false)
    setEditingUser(null)
    reset({
      email: '',
      name: '',
      role: 'agent',
      teamIds: [],
      password: '',
    })
  }

  const onSubmit = async (data: UserFormData) => {
    try {
      if (editingUser) {
        const updateData: UpdateUserData = {
          name: data.name,
          role: data.role,
          teamIds: data.teamIds || [],
        }
        await usersApi.updateUser(editingUser.id, updateData)
        toast.success('Usuario actualizado exitosamente')
      } else {
        const createData: CreateUserData = {
          email: data.email,
          name: data.name,
          role: data.role,
          teamIds: data.teamIds || [],
          password: data.password || undefined,
        }
        await usersApi.createUser(createData)
        toast.success('Usuario creado exitosamente')
      }
      closeModal()
      loadData()
    } catch (error: any) {
      console.error('Error saving user:', error)
      toast.error(error?.response?.data?.message || 'Error al guardar el usuario')
    }
  }

  const handleRemove = async (userId: string) => {
    if (!confirm('¿Estás seguro de que deseas eliminar este usuario de la organización?')) {
      return
    }

    try {
      setRemovingUser(userId)
      await usersApi.removeUser(userId)
      toast.success('Usuario eliminado exitosamente')
      loadData()
    } catch (error: any) {
      console.error('Error removing user:', error)
      toast.error(error?.response?.data?.message || 'Error al eliminar el usuario')
    } finally {
      setRemovingUser(null)
    }
  }

  const getInitials = (name: string | null, email: string) => {
    if (name) {
      return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    }
    return email[0].toUpperCase()
  }

  const filteredUsers = users
    .filter((u) => !tenantId || !u.tenantId || u.tenantId === tenantId)
    .filter((u) => {
      const q = search.toLowerCase()
      return (
        !q ||
        (u.name || '').toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
      )
    })

  const teamOptions: MultiSelectOption[] = teams
    .filter((team) => !tenantId || !team.tenantId || team.tenantId === tenantId)
    .map((team) => ({
      id: team.id,
      label: team.name,
    }))

  const roleBadge = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-emerald-100 text-[#075f55] border border-emerald-200'
      case 'admin':
        return 'bg-sky-100 text-sky-800 border border-sky-200'
      case 'agent':
        return 'bg-indigo-100 text-indigo-800 border border-indigo-200'
      default:
        return 'bg-slate-100 text-slate-700 border border-slate-200'
    }
  }

  const roleShort: Record<string, string> = {
    owner: 'Owner', admin: 'Admin', agent: 'Agent', viewer: 'Viewer',
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#075f55]" />
      </div>
    )
  }

  return (
    <div className="flex flex-col bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">

      {/* Subheader */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-100 px-5 py-4 gap-3 bg-white">
        <div>
          <div className="flex items-center gap-2.5">
            <h3 className="text-base sm:text-lg font-bold text-slate-900">Miembros del Staff</h3>
            <span className="rounded-full bg-slate-100 border border-slate-200 px-2.5 py-0.5 text-xs font-semibold text-slate-600">
              {filteredUsers.length} miembro(s)
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Asesores comerciales y administradores con acceso al panel y atención de chats
          </p>
        </div>
        <Button
          onClick={openCreateModal}
          className="bg-[#075E54] hover:bg-[#064E46] text-white rounded-xl shadow-sm font-semibold px-4 py-2 self-start sm:self-auto"
        >
          <Plus className="mr-1.5 h-4 w-4" />
          Invitar Usuario
        </Button>
      </div>

      {/* Sticky Search */}
      <div className="px-5 py-3 sticky top-0 bg-white z-10 border-b border-slate-100">
        <div className="flex h-11 w-full items-center rounded-xl bg-slate-50 border border-slate-200/80 px-3 focus-within:ring-2 focus-within:ring-[#075E54]/20 focus-within:border-[#075E54] transition-all">
          <Search className="h-4 w-4 text-slate-400 flex-shrink-0" />
          <input
            className="flex-1 bg-transparent pl-2.5 pr-2 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none"
            placeholder="Buscar por nombre o correo electrónico..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {/* Users List */}
      {users.length === 0 ? (
        <div className="p-12 text-center">
          <UserCircle className="mx-auto h-12 w-12 text-slate-200" />
          <h3 className="mt-4 text-sm font-semibold text-slate-700">No hay usuarios registrados</h3>
          <p className="mt-1 text-xs text-slate-400">Comienza invitando a tu primer asesor o administrador</p>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="p-10 text-center">
          <p className="text-sm text-slate-500 font-medium">No se encontraron usuarios coincidentes</p>
          <p className="text-xs text-slate-400 mt-1">Prueba con otro término de búsqueda</p>
        </div>
      ) : (
        <div>
          {filteredUsers.map((user, idx) => (
            <div
              key={user.id}
              className={`flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50/80 transition-colors group ${
                idx > 0 ? 'border-t border-slate-100' : ''
              }`}
            >
              {/* Avatar + active dot */}
              <div className="relative flex-shrink-0">
                <div className="w-11 h-11 rounded-full bg-[#075f55]/10 flex items-center justify-center text-[#075f55] font-bold text-sm border-2 border-white shadow-sm">
                  {getInitials(user.name, user.email)}
                </div>
                <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-emerald-500 border-2 border-white shadow-sm" />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-bold text-slate-900 truncate">
                    {user.name || 'Sin nombre'}
                  </p>
                  <span
                    className={`flex-shrink-0 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${roleBadge(
                      user.role
                    )}`}
                  >
                    {roleShort[user.role] || user.role}
                  </span>
                </div>
                <p className="text-xs text-slate-500 truncate mt-0.5">{user.email}</p>
                {user.teams.length > 0 && (
                  <div className="flex gap-1.5 mt-1.5 flex-wrap">
                    {user.teams.map((team) => (
                      <span
                        key={team.id}
                        className="text-[10px] bg-slate-100 border border-slate-200/60 text-slate-700 px-2 py-0.5 rounded-md font-medium"
                      >
                        {team.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions — visible on touch / hover on desktop */}
              <div className="flex items-center gap-1 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity flex-shrink-0">
                <button
                  onClick={() => openEditModal(user)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-[#075f55] hover:bg-[#075f55]/10 transition-colors"
                  title="Editar"
                >
                  <Edit2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleRemove(user.id)}
                  disabled={removingUser === user.id}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-50"
                  title="Eliminar"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* FAB - Mobile Only */}
      <button
        onClick={openCreateModal}
        className="sm:hidden fixed bottom-20 right-4 z-20 w-12 h-12 rounded-full bg-[#075f55] text-white shadow-xl flex items-center justify-center hover:scale-105 active:scale-95 transition-transform"
        title="Invitar usuario"
      >
        <Plus className="w-5 h-5" />
      </button>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={editingUser ? 'Editar Usuario' : 'Invitar Usuario'}
        size="lg"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-gray-700">
              Email *
            </label>
            <Input
              id="email"
              type="email"
              {...register('email')}
              className="mt-1"
              placeholder="usuario@ejemplo.com"
              disabled={isEditing}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
            )}
            {isEditing && (
              <p className="mt-1 text-xs text-gray-500">
                El email no puede ser modificado
              </p>
            )}
          </div>

          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Nombre *
            </label>
            <Input
              id="name"
              {...register('name')}
              className="mt-1"
              placeholder="Nombre completo"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label htmlFor="role" className="block text-sm font-medium text-gray-700">
              Rol *
            </label>
            <select
              id="role"
              {...register('role')}
              className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#075E54] focus:outline-none focus:ring-1 focus:ring-[#075E54]"
            >
              <option value="owner">Propietario</option>
              <option value="admin">Administrador</option>
              <option value="agent">Agente</option>
              <option value="viewer">Visualizador</option>
            </select>
            {errors.role && (
              <p className="mt-1 text-sm text-red-600">{errors.role.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Asignar a Equipos
            </label>
            <MultiSelect
              options={teamOptions}
              selected={selectedTeamIds}
              onChange={(ids) => setValue('teamIds', ids)}
              placeholder="Seleccionar equipos..."
              searchPlaceholder="Buscar equipos..."
              className="mt-1"
            />
            {selectedTeamIds.length > 0 && (
              <p className="mt-2 text-xs text-gray-500">
                {selectedTeamIds.length} equipo(s) seleccionado(s)
              </p>
            )}
          </div>

          {!isEditing && (
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Contraseña (opcional)
              </label>
              <Input
                id="password"
                type="password"
                {...register('password')}
                className="mt-1"
                placeholder="Dejar vacío para generar automáticamente"
              />
              <p className="mt-1 text-xs text-gray-500">
                Si no se proporciona, se generará una contraseña aleatoria
              </p>
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={closeModal}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? 'Guardando...'
                : editingUser
                  ? 'Actualizar'
                  : 'Invitar Usuario'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

